// Restore-from-shared-URL boot step (#244).
//
// On boot we read the `s=` token from the URL once and decode it into a
// pending-restore map. Each input island, on mount, pulls its own slice by
// inputId and uses it as its initial value instead of the spec default (the
// pull model — mirrors how islands read their data-* defaults in mount.tsx).
// Reading-on-mount avoids any push/broadcast race against island mount order.
//
// Auto-Simulate once the restored controls have committed (#297): the recipient
// came to see the shared result, so we click Simulate for them rather than leave
// the page at baseline. This reverses the original "human click only" stance.
// The guard that mattered was never the click — it is the decode trust boundary
// here plus the server-side range clamp (logic/validate.R); a crafted `?s=` link
// only ever spends the opener's own session compute, which is bounded and cached.
//
// The token can arrive two ways:
//   - app's own URL query     /?s=...            (Copy-link, local/standalone)
//   - forwarded as a hash      #?s=...           (static-site iframe relay,
//     because a hash fragment can't be stripped by the /app_direct/ proxy)

import { decodeShareToken } from "../lib/share_token";
import type { ShareFingerprint } from "../lib/share_token";

let pendingRestore: Record<string, unknown> = {};
let restoreFingerprint: ShareFingerprint | null = null;
let restoreActive = false;

// Auto-run-on-restore bookkeeping (#297). Each restored island reports itself
// via notifyRestored once it has pushed its value to Shiny; when every control
// in the token has reported, we run the simulation once (the recipient came to
// see the result, and it is almost always a cache hit). restoreExpected is the
// token's control ids; restoreReported fills as islands mount; restoreFired
// guards the one-shot. The handler (the actual Simulate click) is injected at
// boot so this module stays free of DOM/Shiny concerns.
let restoreExpected = new Set<string>();
const restoreReported = new Set<string>();
let restoreFired = false;
let restoreCompleteHandler: (() => void) | null = null;

interface UrlParts {
  hash: string;
  search: string;
}

function readTokenFromUrl(parts: UrlParts): string | null {
  // Hash-query first (iframe relay), then the page's own search string.
  const qIndex = parts.hash.indexOf("?");
  if (qIndex !== -1) {
    const fromHash = new URLSearchParams(parts.hash.slice(qIndex + 1)).get("s");
    if (fromHash) return fromHash;
  }
  return new URLSearchParams(parts.search).get("s");
}

/**
 * Read + decode the token once at boot. Idempotent; safe to call before mount.
 * `parts` defaults to the live location; tests pass a synthetic URL.
 */
export function initShareRestore(parts?: UrlParts): void {
  const source =
    parts ??
    (typeof window === "undefined"
      ? { hash: "", search: "" }
      : { hash: window.location.hash, search: window.location.search });
  const decoded = decodeShareToken(readTokenFromUrl(source));
  if (!decoded) return;
  pendingRestore = decoded.controls;
  restoreFingerprint = { app: decoded.app, eng: decoded.eng };
  restoreActive = Object.keys(decoded.controls).length > 0;
  restoreExpected = new Set(Object.keys(decoded.controls));
  restoreReported.clear();
  restoreFired = false;
}

/**
 * Register the action to run once every restored control has reported (#297).
 * Wired at boot to a Simulate click. Registering after all controls already
 * reported (no race in practice, but cheap to be safe) fires immediately.
 */
export function setRestoreCompleteHandler(handler: () => void): void {
  restoreCompleteHandler = handler;
  maybeRunRestoredReform();
}

/**
 * An island calls this in its commit effect, after pushing its value to Shiny.
 * No-op unless this id is one the token restored and we haven't run yet, so
 * islands can call it unconditionally and later user edits are ignored.
 */
export function notifyRestored(inputId: string): void {
  if (restoreFired || !restoreActive) return;
  if (!restoreExpected.has(inputId)) return;
  restoreReported.add(inputId);
  maybeRunRestoredReform();
}

function maybeRunRestoredReform(): void {
  if (restoreFired || !restoreActive || !restoreCompleteHandler) return;
  if (restoreReported.size < restoreExpected.size) return;
  restoreFired = true;
  restoreCompleteHandler();
}

/** The restored value for an island's inputId, or undefined if none. */
export function getPendingControl(inputId: string): unknown {
  return Object.prototype.hasOwnProperty.call(pendingRestore, inputId)
    ? pendingRestore[inputId]
    : undefined;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/**
 * Restored initial value for a single-number control (delta / absolute slider),
 * clamped to the control's range so a hostile token can't push it out of
 * bounds; falls back to the spec default when there's no (valid) restore.
 */
export function restoredNumber(
  inputId: string,
  defaultValue: number,
  min: number,
  max: number,
): number {
  const restored = getPendingControl(inputId);
  if (typeof restored === "number" && Number.isFinite(restored)) {
    return clamp(restored, min, max);
  }
  return defaultValue;
}

/**
 * Restored { boundaries, rates } for a bracket_edit control. Accepts only a
 * value whose arrays match the spec's lengths (drift-safe), clamping each entry
 * to its range; otherwise falls back to the defaults.
 */
export function restoredBrackets(
  inputId: string,
  boundaryDefaults: number[],
  rateDefaults: number[],
  boundaryMin: number,
  boundaryMax: number,
  rateMin: number,
  rateMax: number,
): { boundaries: number[]; rates: number[] } {
  const r = getPendingControl(inputId);
  if (
    isRecord(r) &&
    Array.isArray(r.boundaries) &&
    Array.isArray(r.rates) &&
    r.boundaries.length === boundaryDefaults.length &&
    r.rates.length === rateDefaults.length &&
    r.boundaries.every((v) => typeof v === "number" && Number.isFinite(v)) &&
    r.rates.every((v) => typeof v === "number" && Number.isFinite(v))
  ) {
    return {
      boundaries: r.boundaries.map((v) => clamp(v, boundaryMin, boundaryMax)),
      rates: r.rates.map((v) => clamp(v, rateMin, rateMax)),
    };
  }
  return { boundaries: [...boundaryDefaults], rates: [...rateDefaults] };
}

/**
 * Restored { categoryId -> bandId } commit for a categorical_assignment. Keeps
 * only entries whose category and band still exist in the spec and that differ
 * from the category's default band (identity entries are dropped). Unknown keys
 * from an older spec degrade silently to "unchanged".
 */
export function restoredCategorical(
  inputId: string,
  validBandIds: string[],
  defaultBandOf: Record<string, string>,
): Record<string, string> {
  const r = getPendingControl(inputId);
  if (!isRecord(r)) return {};
  const out: Record<string, string> = {};
  for (const [categoryId, bandId] of Object.entries(r)) {
    if (typeof bandId !== "string") continue;
    if (!Object.prototype.hasOwnProperty.call(defaultBandOf, categoryId))
      continue;
    if (!validBandIds.includes(bandId)) continue;
    if (bandId === defaultBandOf[categoryId]) continue;
    out[categoryId] = bandId;
  }
  return out;
}

/** True when a non-empty reform was restored from the URL this load. */
export function isRestoreActive(): boolean {
  return restoreActive;
}

/** The app/engine versions stamped in the restored token, for a drift notice. */
export function getRestoreFingerprint(): ShareFingerprint | null {
  return restoreFingerprint;
}

// Test seam: reset module state between cases.
export function _resetShareRestoreForTest(): void {
  pendingRestore = {};
  restoreFingerprint = null;
  restoreActive = false;
  restoreExpected = new Set();
  restoreReported.clear();
  restoreFired = false;
  restoreCompleteHandler = null;
}
